import { Button, Divider, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { emptySubjectResultDraft, SubjectResultDraft, WizardValues } from './wizardTypes';

interface Step3Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
  olResults: SubjectResultDraft[];
  setOlResults: (drafts: SubjectResultDraft[]) => void;
  alResults: SubjectResultDraft[];
  setAlResults: (drafts: SubjectResultDraft[]) => void;
}

const SubjectResultsEditor = ({
  title,
  drafts,
  onChange,
}: {
  title: string;
  drafts: SubjectResultDraft[];
  onChange: (drafts: SubjectResultDraft[]) => void;
}) => (
  <Stack spacing={1}>
    <Typography variant="caption" color="text.secondary">
      {title} — add as many subjects as needed
    </Typography>
    {drafts.map((draft) => (
      <Stack key={draft.localId} direction="row" spacing={1} alignItems="center">
        <TextField
          label="Subject"
          size="small"
          fullWidth
          value={draft.subject}
          onChange={(e) =>
            onChange(drafts.map((d) => (d.localId === draft.localId ? { ...d, subject: e.target.value } : d)))
          }
        />
        <TextField
          label="Grade"
          size="small"
          sx={{ width: 120 }}
          value={draft.grade}
          onChange={(e) =>
            onChange(drafts.map((d) => (d.localId === draft.localId ? { ...d, grade: e.target.value } : d)))
          }
        />
        <IconButton size="small" onClick={() => onChange(drafts.filter((d) => d.localId !== draft.localId))}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
    ))}
    <Button
      size="small"
      startIcon={<AddIcon />}
      onClick={() => onChange([...drafts, emptySubjectResultDraft()])}
      sx={{ alignSelf: 'flex-start' }}
    >
      Add subject
    </Button>
  </Stack>
);

export const Step3AcademicDetails = ({
  values,
  setField,
  olResults,
  setOlResults,
  alResults,
  setAlResults,
}: Step3Props) => (
  <Stack spacing={2}>
    <TextField label="School" fullWidth value={values.schoolName} onChange={(e) => setField('schoolName', e.target.value)} />

    <Divider />
    <Typography variant="subtitle2">Ordinary Level</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField
        select
        label="Local / London"
        fullWidth
        value={values.olType}
        onChange={(e) => setField('olType', e.target.value as WizardValues['olType'])}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        <MenuItem value="LOCAL">Local</MenuItem>
        <MenuItem value="LONDON">London</MenuItem>
      </TextField>
      <TextField
        label="Year"
        type="number"
        fullWidth
        value={values.olYear}
        onChange={(e) => setField('olYear', e.target.value ? Number(e.target.value) : '')}
      />
    </Stack>
    <SubjectResultsEditor title="Subjects, grades & results" drafts={olResults} onChange={setOlResults} />

    <Divider />
    <Typography variant="subtitle2">Advanced Level</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField
        select
        label="Local / London"
        fullWidth
        value={values.alType}
        onChange={(e) => setField('alType', e.target.value as WizardValues['alType'])}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        <MenuItem value="LOCAL">Local</MenuItem>
        <MenuItem value="LONDON">London</MenuItem>
      </TextField>
      <TextField
        label="Year"
        type="number"
        fullWidth
        value={values.alYear}
        onChange={(e) => setField('alYear', e.target.value ? Number(e.target.value) : '')}
      />
      <TextField
        select
        label="Stream"
        fullWidth
        value={values.alStream}
        onChange={(e) => setField('alStream', e.target.value as WizardValues['alStream'])}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        <MenuItem value="MATHEMATICS">Mathematics</MenuItem>
        <MenuItem value="SCIENCE">Science</MenuItem>
        <MenuItem value="COMMERCE">Commerce</MenuItem>
        <MenuItem value="ARTS">Arts</MenuItem>
        <MenuItem value="IT">IT</MenuItem>
        <MenuItem value="OTHER">Other</MenuItem>
      </TextField>
    </Stack>
    <SubjectResultsEditor title="Subjects, grades & predicted/final results" drafts={alResults} onChange={setAlResults} />

    <Divider />
    <TextField
      label="Overall GPA (optional)"
      type="number"
      sx={{ maxWidth: 240 }}
      inputProps={{ step: '0.01', min: 0, max: 10 }}
      value={values.overallGpa}
      onChange={(e) => setField('overallGpa', e.target.value ? Number(e.target.value) : '')}
    />
  </Stack>
);
